import type { TStencilStoreSubmission, TStencilHistoryEvent } from '@src/types/Stencil';

import {
  STENCIL_SUBMISSION_SET_AUTHOR_EMAIL,
  STENCIL_SUBMISSION_SET_AUTHOR_NAME,
  STENCIL_SUBMISSION_SET_CAPTION_TEXT,
  STENCIL_SUBMISSION_SET_OPT_IN_NEWSLETTER,
  STENCIL_SUBMISSION_SET_REMOTE_PREVIEW_IMAGE_URL,
  STENCIL_SUBMISSION_SET_SHARE_WITH_OTHERS
} from '@src/constants/stencil';
import {
  WYSIWYG_EDITOR_ADD_PAGE_CONTENT_ITEM,
  WYSIWYG_EDITOR_DELETE_PAGE_CONTENT_ITEM,
  WYSIWYG_EDITOR_MOVE_PAGE_CONTENT_ITEM_BACKWARD,
  WYSIWYG_EDITOR_MOVE_PAGE_CONTENT_ITEM_FORWARD,
  WYSIWYG_EDITOR_REPLACE_PAGE_CONTENT_IMAGES,
  WYSIWYG_EDITOR_SET_LAYOUT_TEMPLATE_NAME,
  WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_CONTAINER_LOCATION,
  WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_CONTENT_LOCATION,
  WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_LOCATIONS,
  WYSIWYG_EDITOR_UPDATE_IMAGE_PAGE_CONTENT_ITEM_ZOOM,
  WYSIWYG_EDITOR_UPDATE_PAGE_CONTENT_ITEM,
  WYSIWYG_EDITOR_UPDATE_TEXT_PAGE_CONTENT_ITEM_CONTENT,
  WYSIWYG_EDITOR_UPDATE_TEXT_PAGE_CONTENT_ITEM_BACKGROUND_COLOR,
  WYSIWYG_EDITOR_SAVE_SNAPSHOT,
  WYSIWYG_EDITOR_UNDO,
  WYSIWYG_EDITOR_REDO,
  WYSIWYG_EDITOR_CLEAR_IMAGE,
  WYSIWYG_EDITOR_MOVE_TEXT_CONTENT_TO_CACHE
} from '@src/constants/wysiwyg_editor';
import {
  SetAuthorEmailAction,
  SetAuthorNameAction,
  SetCaptionTextAction,
  SetShareWithOthersAction,
  SetOptInNewsletterAction,
  SetRemotePreviewImageUrlAction
} from '../../actions/stencils/editor';
import {
  AddPageContentItemAction,
  DeletePageContentItemAction,
  MovePageContentItemBackwardAction,
  MovePageContentItemForwardAction,
  MoveTextContentToCacheAction,
  SetLayoutTemplateNameAction,
  RedoAction,
  ReplaceLayoutImageItemsAction,
  SaveSnapshotAction,
  UndoAction,
  UpdateContentItemAction,
  UpdateContentItemContentLocationAction,
  UpdateContentItemContainerLocationAction,
  UpdateContentItemLocationsAction,
  UpdateTextContentItemContentAction,
  UpdateTextContentItemBackgroundColorAction,
  UpdateImageContentItemZoomAction,
  ClearImageAction
} from '../../actions/wysiwyg/editor';

import { handleError } from '@src/utils/error';
import {
  areAllImagesPopulated,
  contentItemModifiers,
  doesItemSeemValid,
  enforcePageContentOrder
} from '@src/utils/wysiwyg_editor/data';

export type Action =
  | AddPageContentItemAction
  | ClearImageAction
  | DeletePageContentItemAction
  | MovePageContentItemBackwardAction
  | MovePageContentItemForwardAction
  | MoveTextContentToCacheAction
  | SaveSnapshotAction
  | SetAuthorEmailAction
  | SetAuthorNameAction
  | SetCaptionTextAction
  | SetShareWithOthersAction
  | SetLayoutTemplateNameAction
  | SetOptInNewsletterAction
  | SetRemotePreviewImageUrlAction
  | RedoAction
  | ReplaceLayoutImageItemsAction
  | UndoAction
  | UpdateContentItemAction
  | UpdateContentItemContentLocationAction
  | UpdateContentItemContainerLocationAction
  | UpdateContentItemLocationsAction
  | UpdateTextContentItemContentAction
  | UpdateTextContentItemBackgroundColorAction
  | UpdateImageContentItemZoomAction;

const initialState: TStencilStoreSubmission = {
  allImagesPopulated: false,
  authorEmail: '',
  authorName: null,
  captionText: null,
  history: { undo: [], redo: [] },
  layoutTemplateName: '',
  optInNewsletter: false,
  originalUuidForPreview: null,
  pageContentItems: [],
  remotePreviewImageUrl: null,
  shareWithOthers: false
};

function newHistoryEvent(state: TStencilStoreSubmission): TStencilHistoryEvent {
  return {
    authorName: state.authorName,
    allImagesPopulated: state.allImagesPopulated,
    captionText: state.captionText,
    layoutTemplateName: state.layoutTemplateName,
    pageContentItems: state.pageContentItems
  };
}

function submission(state: TStencilStoreSubmission = initialState, action: Action): TStencilStoreSubmission {
  if (action.type === WYSIWYG_EDITOR_ADD_PAGE_CONTENT_ITEM) {
    if (!doesItemSeemValid(action.payload)) {
      handleError(new Error(`Tried to add invalid page content item: ${JSON.stringify(action.payload)}`));

      return state;
    }

    const newItems = contentItemModifiers[action.type](state.pageContentItems, action);

    return {
      ...state,
      layoutTemplateName: 'custom',
      pageContentItems: newItems,
      allImagesPopulated: areAllImagesPopulated(newItems)
    };
  } else if (action.type === WYSIWYG_EDITOR_DELETE_PAGE_CONTENT_ITEM) {
    const newItems = contentItemModifiers[action.type](state.pageContentItems, action);

    return {
      ...state,
      layoutTemplateName: 'custom',
      pageContentItems: newItems,
      allImagesPopulated: areAllImagesPopulated(newItems)
    };
  } else if (action.type === WYSIWYG_EDITOR_MOVE_PAGE_CONTENT_ITEM_BACKWARD) {
    const newItems = contentItemModifiers[action.type](state.pageContentItems, action);

    return { ...state, pageContentItems: newItems };
  } else if (action.type === WYSIWYG_EDITOR_MOVE_PAGE_CONTENT_ITEM_FORWARD) {
    const newItems = contentItemModifiers[action.type](state.pageContentItems, action);

    return { ...state, pageContentItems: newItems };
  } else if (action.type === WYSIWYG_EDITOR_REPLACE_PAGE_CONTENT_IMAGES) {
    const newItems = contentItemModifiers[action.type](state.pageContentItems, action);

    return {
      ...state,
      pageContentItems: enforcePageContentOrder(newItems),
      allImagesPopulated: areAllImagesPopulated(newItems)
    };
  } else if (action.type === STENCIL_SUBMISSION_SET_AUTHOR_NAME) {
    return { ...state, authorName: action.payload };
  } else if (action.type === STENCIL_SUBMISSION_SET_AUTHOR_EMAIL) {
    return { ...state, authorEmail: action.payload };
  } else if (action.type === STENCIL_SUBMISSION_SET_CAPTION_TEXT) {
    return { ...state, captionText: action.payload };
  } else if (action.type === WYSIWYG_EDITOR_SET_LAYOUT_TEMPLATE_NAME) {
    return { ...state, layoutTemplateName: action.payload };
  } else if (action.type === STENCIL_SUBMISSION_SET_OPT_IN_NEWSLETTER) {
    return { ...state, optInNewsletter: action.payload };
  } else if (action.type === STENCIL_SUBMISSION_SET_REMOTE_PREVIEW_IMAGE_URL) {
    return { ...state, remotePreviewImageUrl: action.payload };
  } else if (action.type === STENCIL_SUBMISSION_SET_SHARE_WITH_OTHERS) {
    return { ...state, shareWithOthers: action.payload };
  } else if (action.type === WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_CONTENT_LOCATION) {
    const newPageItems = contentItemModifiers[action.type](state.pageContentItems, action);

    return {
      ...state,
      pageContentItems: newPageItems
    };
  } else if (action.type === WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_CONTAINER_LOCATION) {
    const newPageItems = contentItemModifiers[action.type](state.pageContentItems, action);

    return {
      ...state,
      pageContentItems: newPageItems
    };
  } else if (action.type === WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_LOCATIONS) {
    const newPageItems = contentItemModifiers[action.type](state.pageContentItems, action);

    return {
      ...state,
      pageContentItems: newPageItems
    };
  } else if (action.type === WYSIWYG_EDITOR_UPDATE_PAGE_CONTENT_ITEM) {
    const newPageItems = contentItemModifiers[action.type](state.pageContentItems, action);

    return {
      ...state,
      pageContentItems: newPageItems,
      allImagesPopulated: areAllImagesPopulated(newPageItems)
    };
  } else if (action.type === WYSIWYG_EDITOR_UPDATE_TEXT_PAGE_CONTENT_ITEM_CONTENT) {
    const newPageItems = contentItemModifiers[action.type](state.pageContentItems, action);

    return {
      ...state,
      pageContentItems: newPageItems
    };
  } else if (action.type === WYSIWYG_EDITOR_UPDATE_TEXT_PAGE_CONTENT_ITEM_BACKGROUND_COLOR) {
    const newPageItems = contentItemModifiers[action.type](state.pageContentItems, action);

    return {
      ...state,
      pageContentItems: newPageItems
    };
  } else if (action.type === WYSIWYG_EDITOR_UPDATE_IMAGE_PAGE_CONTENT_ITEM_ZOOM) {
    const newPageItems = contentItemModifiers[action.type](state.pageContentItems, action);

    return {
      ...state,
      pageContentItems: newPageItems
    };
  } else if (action.type === WYSIWYG_EDITOR_SAVE_SNAPSHOT) {
    const newEvent = newHistoryEvent(state);

    return {
      ...state,
      history: {
        ...state.history,
        undo: [...state.history.undo, newEvent],
        redo: []
      }
    };
  } else if (action.type === WYSIWYG_EDITOR_UNDO) {
    const undo = [...state.history.undo];
    const redo = [...state.history.redo];

    if (undo.length === 0) return state;

    const nextState = undo.pop();
    const newEvent = newHistoryEvent(state);

    redo.push(newEvent);

    return {
      ...state,
      ...nextState,
      history: { undo, redo }
    };
  } else if (action.type === WYSIWYG_EDITOR_REDO) {
    const undo = [...state.history.undo];
    const redo = [...state.history.redo];

    if (redo.length === 0) return state;

    const nextState = redo.pop();
    const newEvent = newHistoryEvent(state);

    undo.push(newEvent);

    return {
      ...state,
      ...nextState,
      history: { undo, redo }
    };
  } else if (action.type === WYSIWYG_EDITOR_CLEAR_IMAGE) {
    const newPageItems = contentItemModifiers[action.type](state.pageContentItems, action);

    return {
      ...state,
      pageContentItems: newPageItems
    };
  } else if (action.type === WYSIWYG_EDITOR_MOVE_TEXT_CONTENT_TO_CACHE) {
    const newPageItems = contentItemModifiers[action.type](state.pageContentItems, action);

    return {
      ...state,
      pageContentItems: newPageItems
    };
  }

  return state;
}

export default submission;
